/**
 * Parse the marketplace cart's `timesOutAtUtc` into epoch milliseconds.
 *
 * Why this is not `new Date(value)` (FAS-1376): ticket-api used to send this field WITHOUT an
 * offset ("2026-09-10T12:50:15.336865"). An ISO date-time without an offset is parsed by
 * `new Date()` as the reader's LOCAL time, so the countdown was off by the visitor's UTC offset.
 * The value is UTC by contract (the name says so), so we read the wall-clock parts ourselves and
 * hand them to `Date.UTC`.
 *
 * ticket-api now appends the offset ("…336865Z"). Deploy order between the api and this shop is
 * not guaranteed, so both spellings must keep working:
 *   - naive            2026-09-10T12:50:15.336865   → read as UTC
 *   - with `Z`         2026-09-10T12:50:15.336865Z  → UTC
 *   - with an offset   2026-09-10T14:50:15+02:00    → honoured
 * Seconds and the fraction are optional: the api (Brick's toISOString) drops `:ss` when it is
 * zero and the fraction when it is zero ("2026-09-10T12:50Z"). The fraction may have up to six
 * digits; only milliseconds are kept.
 *
 * Returns NaN for anything else, so callers can treat it as "no deadline".
 */
const PATTERN =
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?$/i;

export function parseUtcTimestamp(value: string): number {
    const match = PATTERN.exec(value.trim());
    if (!match) return NaN;

    const [, year, month, day, hours, minutes, seconds = '0', fraction = '', offset] = match;
    const millis = Number(fraction.slice(0, 3).padEnd(3, '0'));

    const wallClockAsUtc = Date.UTC(
        Number(year),
        Number(month) - 1, // JavaScript months are 0-based
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds),
        millis,
    );

    if (!offset || offset.toUpperCase() === 'Z') return wallClockAsUtc;

    const sign = offset.startsWith('-') ? -1 : 1;
    const digits = offset.slice(1).replace(':', '');
    const offsetMinutes = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4));

    return wallClockAsUtc - sign * offsetMinutes * 60_000;
}
